import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { isCancel } from '@/utils/axios';
import { hospitalHeaders } from '@/utils';
import type { NotificationItem, NotificationsListResponse, NotificationStatus, UnreadCountResponse } from '@/types/notification';
import { getErrorMessage } from '@/utils/error-message';

export interface FetchNotificationsParams {
    status?: NotificationStatus;
    page?: number;
    limit?: number;
    hospitalId?: string;
    append?: boolean;
}

export interface MarkNotificationAsReadParams {
    id: string;
    hospitalId?: string;
}

const fetchNotificationUnreadCount = async (hospitalId?: string): Promise<{ count: number }> => {
    const { data } = await axiosInstance.get<UnreadCountResponse>('notifications/unread-count', hospitalHeaders(hospitalId));
    return data;
};

export const fetchNotifications = createAsyncThunk<
    {
        items: NotificationsListResponse['items'];
        total: number;
        page: number;
        limit: number;
        status?: NotificationStatus;
    },
    FetchNotificationsParams | undefined,
    { rejectValue: string }
>('notifications/fetchNotifications', async (params, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<NotificationsListResponse>('notifications', {
            params: {
                status: params?.status || undefined,
                page: params?.page ?? 1,
                limit: params?.limit ?? 50
            },
            ...hospitalHeaders(params?.hospitalId),
            signal
        });

        return {
            items: data.items ?? [],
            total: data.total ?? 0,
            page: data.page ?? params?.page ?? 1,
            limit: data.limit ?? params?.limit ?? 50,
            status: params?.status
        };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch notifications'));
    }
});

export const markNotificationAsRead = createAsyncThunk<NotificationItem, MarkNotificationAsReadParams, { rejectValue: string }>(
    'notifications/markAsRead',
    async ({ id, hospitalId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.patch<NotificationItem>(
                `notifications/${encodeURIComponent(id)}/read`,
                undefined,
                hospitalHeaders(hospitalId)
            );
            return data;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to mark notification as read'));
        }
    }
);

export { fetchNotificationUnreadCount };
